import React, { useState } from 'react';
import classnames from 'classnames';
import { Button } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import ModalBase, { BaseModalProps } from '../../ModalBase/ModalBase';
import styles from './Confirmation.module.scss';

import Spinner from 'legacy/common/components/spinner/Spinner';

interface ConfirmationProps {
  baseModalProps: BaseModalProps;
  title?: string;
  message?: string;
  actionText?: string;
  cancelText?: string;
  action?: () => void;
  hideBackButton?: boolean;
  destructive?: boolean;
  onCancel?: () => void;
  onSuccess: (args: { close: () => void }) => void;
  close: () => void;
  type?: 'success' | string;
  showLoading?: boolean;
  disableCloseOnCancel?: boolean;
  disableCloseOnAction?: boolean;
}

const Confirmation: React.FC<ConfirmationProps> = ({
  baseModalProps,
  title,
  message,
  actionText,
  cancelText,
  action,
  hideBackButton,
  destructive,
  onCancel,
  onSuccess,
  close,
  type,
  showLoading,
  disableCloseOnCancel,
  disableCloseOnAction
}) => {
  const { t } = useTranslation(['common']);
  const [isLoading, setLoading] = useState<boolean>(false);

  const handleConfirm = () => {
    if (action) {
      action();
    }

    if (showLoading) {
      setLoading(true);
    } else {
      if (disableCloseOnAction) {
        return;
      }

      onSuccess({ close });
    }
  };

  const handleCancel = () => {
    if (!disableCloseOnCancel) {
      close();
    }

    if (onCancel) {
      onCancel();
    }
  };

  return (
    <ModalBase
      baseModalProps={{
        ...baseModalProps,
        shouldCloseOnOverlayClick: !isLoading,
        shouldCloseOnEsc: !isLoading
      }}
      back_img="Back"
      contentClassName="confirmation"
      className={classnames({ success: type === 'success' })}
      hideBackButton={hideBackButton}
    >
      <div className="confirmationMessage">
        {title && <p className={styles.title}>{title}</p>}
        <p>{message || t('common:alert.areYouSure')}</p>
      </div>

      <div className="actions">
        <Button
          disabled={isLoading}
          onClick={handleCancel}
          data-cy="modal-cancel-button"
        >
          {cancelText || t('common:buttons.cancel')}
        </Button>
        <Button
          disabled={isLoading}
          negative={destructive}
          primary={!destructive}
          onClick={handleConfirm}
          data-cy="modal-confirm-button"
        >
          {isLoading ? <Spinner /> : actionText || t('common:buttons.yes')}
        </Button>
      </div>
    </ModalBase>
  );
};



export default Confirmation;
